import COLORS from '@/constants/colors';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import {
  Box,
  IconButton,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  DEFAULT_PAGING_BOUNDARY_COUNT,
  DEFAULT_SIBILING_BOUNDARY_COUNT,
  TableDataProperties,
} from './TableDataUi';
import { useTableDataViewController } from './TableDataViewController';
import TableNoData from './TableNoData';

export function TableData<T>({
  columnHeaders,
  items,
  onRowClick,
  rowItemGenerator,
  paging,
  onPageSelected: onPageRequested,
  roundedTop,
  compact,
}: TableDataProperties<T>) {
  const { uiState, actions, references } = useTableDataViewController(items);

  return (
    <Box>
      {(uiState.showLeftArrow || uiState.showRightArrow) && (
        <Box
          sx={{
            height: '32px',
            position: 'relative',
            backgroundColor: 'white',
            borderBottom: `1px solid ${COLORS.gray_6}`,
            borderTopLeftRadius: '10px',
            borderTopRightRadius: '10px',
          }}
        >
          {uiState.showLeftArrow && (
            <IconButton
              onClick={() => actions.scrollBy(-1)}
              sx={{
                position: 'absolute',
                left: 0,
                top: '50%',
                transform: 'translateY(-50%)',
              }}
            >
              <ArrowBackIos fontSize="small" />
            </IconButton>
          )}
          {uiState.showRightArrow && (
            <IconButton
              onClick={() => actions.scrollBy(1)}
              sx={{
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
              }}
            >
              <ArrowForwardIos fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}
      <Box
        ref={references.headerRef}
        sx={{ overflowX: 'auto', overflowY: 'hidden' }}
      >
        <Box sx={{ width: uiState.syncWidth, height: '1px' }} />
      </Box>
      <TableContainer
        ref={references.containerRef}
        sx={{
          borderTopLeftRadius: roundedTop ? '8px' : undefined,
          borderTopRightRadius: roundedTop ? '8px' : undefined,
          overflowX: 'auto',
        }}
      >
        <Table
          sx={{
            borderCollapse: 'collapse',
            '& > * > *': { border: 'none' },
            borderSpacing: 0,
            '& .MuiTableRow-root > *': {
              borderBottom: `1px solid ${COLORS.gray_6}`,
              py: compact ? '8px' : '16px',
            },
            '& .MuiTableCell-head': { padding: '5px 24px', textAlign: 'left' },
            tableLayout: 'auto',
          }}
        >
          <TableHead>
            <TableRow sx={{ backgroundColor: COLORS.gray_100 }}>
              {columnHeaders.map((header, index) => (
                <TableCell
                  key={index}
                  sx={{
                    color: COLORS.gray_4,
                    height: '44px',
                    whiteSpace: 'nowrap',
                    textAlign: 'center',
                  }}
                >
                  {header.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, rowIndex) => (
              <TableRow
                key={`row_${rowIndex}`}
                sx={{
                  cursor: onRowClick ? 'pointer' : 'inherit',
                  width: '100%',
                  zIndex: 10,
                }}
                onClick={() => onRowClick?.(item)}
              >
                {rowItemGenerator(item).map((cell, cellIndex) => (
                  <TableCell
                    key={`cell_${rowIndex}-${cellIndex}`}
                    sx={{ pl: 3 }}
                  >
                    {cell}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {items.length === 0 && <TableNoData message="Sin resultados" />}
      </TableContainer>
      {paging && (
        <Stack sx={{ py: '32px' }}>
          <Pagination
            sx={{ margin: 'auto' }}
            count={paging.pageCount}
            page={paging.currentPage}
            color="primary"
            onChange={(_event, value) => onPageRequested?.(value)}
            boundaryCount={
              paging.boundaryCount ?? DEFAULT_PAGING_BOUNDARY_COUNT
            }
            siblingCount={
              paging.siblingCount ?? DEFAULT_SIBILING_BOUNDARY_COUNT
            }
          />
        </Stack>
      )}
    </Box>
  );
}
